// Third Party Imports
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Toaster} from '@homeaway/react-toaster';

// Relative Imports
import CarouselActions from '../../actions/carouselActions';
import ContactOwnerFormActions from '../../actions/contactOwnerFormActions';
import ListingActions from '../../actions/listingActions';
import ListingPageActions from '../../actions/listingPageActions';
import ReviewActions from '../../actions/reviewsActions';
import WindowActions from '../../actions/windowActions';
import EdapActions from '../../actions/edapActions';
import InquiryModalActions from '../../actions/inquiryModalActions';
import RequestToBookModalActions from '../../actions/requestToBookModalActions';
import TripActions from '../../actions/tripActions';
import RouterActions from '../../actions/routerActions';
import TravelerActions from '../../actions/travelerActions';
import getListingPageData from '../../selectors/listingPage';

import windowResizeHandler from '../../utils/windowResizeHandler';
import errorHandler from '../../utils/errorHandler';
import {triggerErrorEdapEvent} from '../../utils/errorEvents';
import {logPrimaryActionRendered} from '../../utils/logPrimaryActionRendered';
import {writeCookie} from '../../utils/cookieHelpers';
import {getQueryParams} from '../../utils/queryStringHelper';
import {availableRoutes} from '../../routes';
import ListingPageAll from './ListingPageAll';

/* global ha */
/* istanbul ignore next */
function mapStateToProps(state) {
    //  Use memoized selectors to compose what drives this container.
    return getListingPageData(state);
}

/* istanbul ignore next */
function mapDispatchToProps(dispatch) {
    // http://redux.js.org/docs/api/bindActionCreators.html
    // "you want to pass some action creators down to a component that isn't aware of Redux"
    // TODO: remember a better way to compose the actions.
    const carouselActions = bindActionCreators(CarouselActions, dispatch);
    const contactOwnerFormActions = bindActionCreators(ContactOwnerFormActions, dispatch);
    const listingActions = bindActionCreators(ListingActions, dispatch);
    const listingPageActions = bindActionCreators(ListingPageActions, dispatch);
    const reviewsActions = bindActionCreators(ReviewActions, dispatch);
    const windowActions = bindActionCreators(WindowActions, dispatch);
    const edapActions = bindActionCreators(EdapActions, dispatch);
    const inquiryModalActions = bindActionCreators(InquiryModalActions, dispatch);
    const requestToBookModalActions = bindActionCreators(RequestToBookModalActions, dispatch);
    const tripActions = bindActionCreators(TripActions, dispatch);
    const routerActions = bindActionCreators(RouterActions, dispatch);
    const travelerActions = bindActionCreators(TravelerActions, dispatch);

    return {actions: {
        carouselActions,
        checkIfFromListingPage: routerActions.checkIfFromListingPage,
        expandReviewClicked: reviewsActions.expandReviewClicked,
        inquiryClicked: listingPageActions.inquiryClicked,
        inquiryModalRequestToBook: inquiryModalActions.inquiryModalRequestToBook,
        inquiryModalSubmit: inquiryModalActions.inquirySubmit,
        logPriceSummaryEvent: listingPageActions.logPriceSummaryEvent,
        logTestExposure: edapActions.logTestExposure,
        logToServer: listingActions.logToServer,
        navigateBackTo: routerActions.navigateBackTo,
        navigateTo: routerActions.navigateTo,
        pageLoaded: listingActions.pageLoaded,
        primaryCtaHandler: listingActions.primaryCtaActivated,
        processServerSideEvaluatedTests: listingPageActions.processServerSideEvaluatedTests,
        requestToBookModalSubmit: requestToBookModalActions.requestToBookModalSubmit,
        scrollInquiryTop: contactOwnerFormActions.scrollTop,
        scrollReviewsTop: reviewsActions.scrollTop,
        setAsFavorite: listingActions.setAsFavorite,
        setBookingFlow: tripActions.setBookingFlow,
        setInquiryModalOpen: listingActions.setInquiryModalOpen,
        setCivilCodeDisclaimerModalOpen: listingActions.setCivilCodeDisclaimerModalOpen,
        setLoginModalOpen: listingActions.setLoginModalOpen,
        setInquiryLoginModalOpen: listingActions.setInquiryLoginModalOpen,
        setRequestToBookModalOpen: listingActions.setRequestToBookModalOpen,
        setReviewPageIndex: reviewsActions.setPageIndex,
        setStickyPosition: windowActions.setStickyPosition,
        setUserLoggedIn: travelerActions.setUserLoggedIn,
        setVirtualTourModalOpen: listingActions.setVirtualTourModalOpen,
        enterDatesTimerComplete: listingPageActions.enterDatesTimerComplete,
        triggerEdapEvent: edapActions.triggerEdapEvent,
        triggerEdapListingBooknowClick: edapActions.triggerEdapListingBooknowClick,
        tripDetailsEdapCheck: tripActions.tripDetailsEdapCheck,
        updateTripDataCookie: listingActions.updateTripDataCookie,
        viewPriceDetailsClicked: listingPageActions.viewPriceDetailsClicked,
        windowScrolled: windowActions.windowScrolled,
        windowSizeChanged: windowActions.windowSizeChanged
    }};
}

@connect(mapStateToProps, mapDispatchToProps)
@errorHandler
@windowResizeHandler
class ListingPageContainer extends Component {
    constructor(props) {
        super(props);

        this.timingConstructor = +(new Date());
        this.loaded = false;
    }

    componentDidMount() {
        this.loaded = true;
        this.timingComponentDidMount = +(new Date());
        this.logPar();

        const queryParams = getQueryParams(window.location.search);
        if (queryParams.noDates !== 'true' && queryParams.noGuests !== 'true') {
            this.setTripCookie();
        }

        this.props.actions.pageLoaded();
        this.checkForErrors();

        const affiliateCookieName = 'ha_affiliate_broker';
        const affiliateCookieValue = this.props.system.cookies[affiliateCookieName];
        if (affiliateCookieValue) {
            writeCookie(affiliateCookieName, affiliateCookieValue, false);
        }

        // Overriding the popstate handler to ensure no one else is handling it
        window.onpopstate = () => {
            this.props.actions.checkIfFromListingPage();
        };

        this.props.actions.processServerSideEvaluatedTests();

        setTimeout(() => {
            this.props.actions.enterDatesTimerComplete(true);
        }, 2500);
    }

    componentWillUnmount() {
        window.onpopstate = null;
    }

    /* eslint-disable complexity */
    logPar = () => {
        const {system, abacusTestData: {'ha_pdp_mweb_perf_insights': mWebPerformanceTest}} = this.props;

        const renderTime = this.timingComponentDidMount - this.timingConstructor;
        const responseEndedAnalytics = ha.userTiming.getEntriesByName('responseEnded');
        const responseEnded = Math.ceil(responseEndedAnalytics.length ? responseEndedAnalytics[0].duration : 0);
        const indexInitializeAnalytics = ha.userTiming.getEntriesByName('index.initialize');
        const indexInitialize = Math.ceil(indexInitializeAnalytics.length ? indexInitializeAnalytics[0].startTime : 0);
        const filesDownloadedAndParsed = indexInitialize - responseEnded;

        ha.userTiming.mark('component.did.mount');
        const componentDidMountAnalytics = ha.userTiming.getEntriesByName('component.did.mount');
        const componentDidMount = Math.ceil(componentDidMountAnalytics.length ? componentDidMountAnalytics[0].startTime : 0);

        this.props.actions.logToServer({
            renderTime,
            filesDownloadedAndParsed,
            isMobile: system.isMobile,
            isDesktop: system.isDesktop,
            isTablet: system.isTablet,
            userAgent: system.userAgent,
            clientDesktopRenderTime: !system.isMobile ? renderTime : null,
            clientMobileRenderTime: system.isMobile ? renderTime : null,
            componentDidMount,
            performanceTestVariant: mWebPerformanceTest && mWebPerformanceTest.value
        });

        logPrimaryActionRendered();
    };

    /* eslint-enable complexity */

    setTripCookie = () => {
        const {
            actions
        } = this.props;

        actions.updateTripDataCookie();
    };

    checkForErrors() {
        const {
            bookingModalData,
            actions
        } = this.props;

        if (bookingModalData.currentError) {
            triggerErrorEdapEvent(actions.triggerEdapEvent, bookingModalData.currentError);
        }
    }

    render() {
        const {
            actions,
            ...rest
        } = this.props;
        const {listingPageComponent} = rest;

        return (
            <div className="listing-page-container">
                <ListingPageAll
                    listingPageComponent={listingPageComponent}
                    availableRoutes={availableRoutes}
                    {...actions}
                    {...rest}
                />
                <Toaster />
            </div>
        );
    }
}

export default ListingPageContainer;
